import React, { useEffect, useState } from "react";
import "./ring-swipe.scss";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { createStructuredSelector } from "reselect";
import store from "../../redux/store";
import { Link, useNavigate } from "react-router-dom";

// external library
import { RingSwipeDeck } from "@rinderr/shared/ring-swipe";

// firebase
import {
  addLikeToRing,
  addDislikeToRing,
  addRingToViewedForUser,
  addRingToLikedForUser,
  addRingToDislikedForUser,
  setViewedRingsForUser,
  setLikedRingsForUser,
  fetchRingsByIds,
  analytics,
} from "../../utils/firebase.utils";

// selectors and actions
import { selectCurrentUser } from "../../redux/user/user.selectors";
import { selectLikedRingUrls, selectLikedRings } from "../../redux/rings/rings.selectors";
import { addLikedRing, addLikedRingUrl, setLikedRings } from "../../redux/rings/rings.actions";

// icons
import { ReactComponent as HeartIcon } from "../../assets/heart_outline.svg";
import { ReactComponent as CrossIcon } from "../../assets/tilted_cross.svg";
import { ReactComponent as TagIcon } from "../../assets/sales_tag.svg";
import { ReactComponent as LeftArrowIcon } from "../../assets/left_arrow.svg";
import { ReactComponent as RightArrowIcon } from "../../assets/right_arrow.svg";

// components
import UploaderTag from "../uploader-tag/uploader-tag";
import OverlayModal from "../overlay-modal/overlay-modal";
import RingDetailedView from "../ring-detailed-view/ring-detailed-view";
import { RINGS_URL } from "../../utils/constants.utils";
import { logEvent } from "firebase/analytics";
import { ICurrentUser, IDirection, ILikeDislike, IRing } from "../../utils/interfaces";
import { useTranslation } from "react-i18next";
import { getUrlForAffiliate } from "../../utils/affiliate.utils";
import CommentsContainer from "../comments-container/comments-container";

// global vars
let alreadyRemoved: string[] = [];
let localLikedRingsState: {}[] = [];
let handledRing = "";

interface IRingSwipe {
  NoMoreRingsToFetch: boolean;
  addLikedRingUrlState: (ringUrl: string) => void;
  setLikedRingsState: (rings: any) => void;
  addLikedRingState: (ring: any) => void;
  ringsFromDb: IRing[];
  currentUser: ICurrentUser;
  setAllFiltersToTrue: () => void;
  fetchRings: () => void;
  collectionId?: string;
  curatedMode?: boolean;
  curatedReasons?: (ring: IRing) => string[];
}

const RingSwipe = ({
  NoMoreRingsToFetch,
  addLikedRingUrlState,
  setLikedRingsState,
  addLikedRingState,
  ringsFromDb,
  currentUser,
  setAllFiltersToTrue,
  fetchRings,
  collectionId,
  curatedMode = false,
  curatedReasons,
}: IRingSwipe) => {
  const [rings, setRings] = useState<IRing[]>(ringsFromDb);
  const [onlyTenRings, setOnlyTenRings] = useState<IRing[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [country, setCountry] = useState<string | null>(null);
  const [ipAddress, setIpAddress] = useState<string | null>(null);
  let navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (currentUser !== null) return;
    const fetchIpAndCountry = async () => {
      try {
        const response = await fetch("https://getcountryfromip-eult3duyqq-uc.a.run.app");
        const data = await response.json();
        setIpAddress(data.ip);
        setCountry(data.country);
      } catch (error) {
        console.error("Error fetching IP and country:", error);
      }
    };

    fetchIpAndCountry();
  }, []);

  useEffect(() => {
    const fetchViewedRingsFromUser = async () => {
      const localViewedRings = localStorage.getItem("alreadyRemovedv2")
        ? JSON.parse(localStorage.getItem("alreadyRemovedv2") as string)
        : [];
      let viewedRings = currentUser.viewedRings;

      if (localViewedRings.length > 0) {
        viewedRings = [...viewedRings, ...localViewedRings];
        const uniqueViewedRings: string[] = Array.from(new Set(viewedRings));
        await setViewedRingsForUser(uniqueViewedRings, currentUser.id);
        localStorage.removeItem("alreadyRemovedv2");
      }

      return viewedRings;
    };

    const updateLikedRingsForUser = async () => {
      const localLikedRings = JSON.parse(localStorage.getItem("likedRings") || "[]");
      let usersLikedRings: (string | ILikeDislike)[] = currentUser.likedRings || [];

      if (localLikedRings.length > 0) {
        localStorage.removeItem("likedRings");
        usersLikedRings = [...usersLikedRings, ...localLikedRings];

        const uniqueRingIds: string[] = Array.from(
          new Set(usersLikedRings.map((ring) => (typeof ring === "string" ? ring : ring.id))),
        );

        const uniqueUsersLikedRings: string[] = uniqueRingIds
          .map((id) =>
            usersLikedRings.find((ring) =>
              typeof ring === "string" ? id === ring : id === ring.id,
            ),
          )
          .filter((ring): ring is string | ILikeDislike => ring !== undefined)
          .map((ring) => (typeof ring === "string" ? ring : ring.id));

        await setLikedRingsForUser(uniqueUsersLikedRings, currentUser.id);
        const likedRingsObjects = await fetchRingsByIds(uniqueRingIds);
        setLikedRingsState(likedRingsObjects);
      }
    };

    const fetchData = async () => {
      if (currentUser) {
        if ("viewedRings" in currentUser) {
          alreadyRemoved = await fetchViewedRingsFromUser();
        }
        // If liked rings are in local storage, update them for the current user
        if (localStorage.getItem("likedRings")) {
          await updateLikedRingsForUser();
        }
      }
      // If  aldreadyRemoved rings are in local storage, filter the list of rings
      else if (localStorage.getItem("alreadyRemovedv2")) {
        alreadyRemoved = JSON.parse(localStorage.getItem("alreadyRemovedv2") as string);
      }

      console.log(ringsFromDb);

      const filteredRings = ringsFromDb.filter((ring: IRing) => !alreadyRemoved.includes(ring.id));

      setRings(filteredRings);
    };

    fetchData();
  }, [ringsFromDb, currentUser, setLikedRingsState]);

  useEffect(() => {
    setOnlyTenRings(rings.slice(0, 10).reverse());
    if (rings.length < 10 && !NoMoreRingsToFetch) fetchRings();
  }, [rings]);

  const outOfFrame = async (direction: IDirection, ringToHandle: IRing) => {
    if (handledRing === ringToHandle.id) return;
    handledRing = ringToHandle.id;

    setRings((ringsState: IRing[]) => ringsState.filter((ring) => ring.id !== ringToHandle.id));

    addRingToViewed(ringToHandle.id);
    switch (direction) {
      case "right":
        await addLikedRing(ringToHandle);
        break;
      case "up":
        await addLikedRing(ringToHandle);
        break;
      case "down":
        await addDislikedRing(ringToHandle);
        break;
      case "left":
        await addDislikedRing(ringToHandle);
        break;
      default:
        break;
    }
  };

  const addLikedRing = async (ringToHandle: IRing) => {
    await addLikedRingUrlState(RINGS_URL + ringToHandle.id);
    await addLikedRingState(ringToHandle);
    localLikedRingsState.push({ id: ringToHandle.id, date: Date.now() });
    const _user = (store.getState().user || {}).currentUser;
    await addLikeToRing(ringToHandle.id, _user, country, ipAddress);
    if (_user) await addRingToLikedForUser(ringToHandle.id, _user.id);
    else localStorage.setItem("likedRings", JSON.stringify(localLikedRingsState));
  };

  const addDislikedRing = async (ringToHandle: IRing) => {
    const _user = (store.getState().user || {}).currentUser;
    await addDislikeToRing(ringToHandle.id, _user, country, ipAddress);
    if (_user) await addRingToDislikedForUser(ringToHandle.id, _user.id);
  };

  const addRingToViewed = async (id: string) => {
    const _user = (store.getState().user || {}).currentUser;
    alreadyRemoved.push(id);
    if (_user) await addRingToViewedForUser(id, _user.id);
    else localStorage.setItem("alreadyRemovedv2", JSON.stringify(alreadyRemoved));
  };

  const toggleModal = () => {
    setShowModal((oldState) => {
      if (oldState === false)
        window.history.pushState(null, "", `/ring/${onlyTenRings[onlyTenRings.length - 1].id}`);
      else window.history.pushState(null, "", "/");
      return !oldState;
    });
  };


  const navigateToRing = (ringParam?: IRing) => {
    const isMobile = window.innerWidth <= 768;
    const ring: IRing | undefined = ringParam ?? onlyTenRings[onlyTenRings.length - 1];

    if (!ring) return;

    if (isMobile) {
      navigate(`/ring/${ring.id}`, {
        state: { ring: ring },
      });
    } else {
      toggleModal();
    }
  };


  const emptyState = !rings.length && curatedMode ? (
    <div className="card-container__no-more-rings collection">
      <p>
        {t("ringSwipe.That's all the rings in this collection leave your comments below or check out more rings")}
      </p>
      <Link to="/liked-rings">{t("ringSwipe.here")}</Link>
    </div>
  ) : !rings.length && !collectionId ? (
    <div className="card-container__no-more-rings">
      <p>
        {t(
          "ringSwipe.We currently have no more rings in your chosen category please check back later for new rings!",
        )}
      </p>
      <button className="text-button white" onClick={setAllFiltersToTrue}>
        {t("ringSwipe.Reset filters")}
      </button>
      <p>{t("ringSwipe.Enrich the amount of rings on Rinder by adding your own.")}</p>
      <button className="primary" onClick={() => navigate("/upload-ring")}>
        {t("ringSwipe.Add ring")}
      </button>
    </div>
  ) : !rings.length ? (
    <div className="card-container__no-more-rings collection">
      <p>
        {t(
          "ringSwipe.That's all the rings in this collection leave your comments below or check out more rings",
        )}{" "}
        <Link to="/">{t("ringSwipe.here")}</Link>
      </p>
      <CommentsContainer collectionId={collectionId}></CommentsContainer>
    </div>
  ) : null;

  const handleVisitStore = (ring: IRing) => {
    if (!ring.salesUrl) return;
    window.open(
      getUrlForAffiliate(
        ring.salesUrl,
        typeof ring.uploadedBy === "object" ? ring.uploadedBy.id : ring.uploadedBy,
      ),
      "_blank",
    );
    logEvent(analytics, "generate_lead", {
      value: ring.salesPrice,
      currency: ring.currency,
      vendor: typeof ring.uploadedBy === "object" ? ring.uploadedBy.id : ring.uploadedBy,
    });
  };

  return (
    <>
      {showModal && (
        <OverlayModal close={toggleModal}>
          <RingDetailedView
            ring={onlyTenRings[onlyTenRings.length - 1]}
            close={toggleModal}
            isModal={true}
          />
        </OverlayModal>
      )}
      <RingSwipeDeck<IRing>
        cards={onlyTenRings}
        onCardLeftScreen={(direction: IDirection, ring: IRing) => outOfFrame(direction, ring)}
        onMoreInfo={(ring: IRing) => navigateToRing(ring)}
        onVisitStore={handleVisitStore}
        renderTagIcon={() => <TagIcon className="tag-icon pressable" />}
        renderLeftArrowIcon={() => <LeftArrowIcon className="arrow-icon pressable" />}
        renderRightArrowIcon={() => <RightArrowIcon className="arrow-icon pressable" />}
        renderLikeIcon={() => <HeartIcon className="heart-icon pressable" />}
        renderDislikeIcon={() => <CrossIcon className="cross-icon pressable" />}
        visitStoreLabel={t("ringSwipe.visit store")}
        renderCardOverlay={(ring: IRing, index: number) =>
          curatedMode ? (
            <div className="card__curated-overlay">
              <div className="card__curated-progress">
                {Math.max(1, onlyTenRings.length - index)} / {Math.max(1, onlyTenRings.length)}
              </div>
              <div className="card__curated-footer">
                <h3 className="card__curated-title">
                  {typeof ring.uploadedBy === "object" ? ring.uploadedBy.name : ring.imageName}
                </h3>
                <div className="card__curated-reasons">
                  <p>Why we picked this</p>
                  <ul>
                    {(curatedReasons ? curatedReasons(ring) : []).slice(0, 3).map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <UploaderTag
              userId={
                typeof ring.uploadedBy === "object"
                  ? (ring.uploadedBy as { id: string; name: string }).id
                  : undefined
              }
            />
          )
        }
        cardClassName={curatedMode ? "curated" : collectionId ? "collection" : undefined}
        collectionMode={Boolean(collectionId)}
        emptyState={emptyState ?? undefined}
        moreInfoLabel={t("ringSwipe.more info")}
        enableKeyboard={!showModal}
      />
    </>
  );
};

const mapStateToProps = createStructuredSelector({
  likedRingUrls: selectLikedRingUrls,
  currentUser: selectCurrentUser,
  likedRingsState: selectLikedRings,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  addLikedRingUrlState: (ringUrl: string) => dispatch(addLikedRingUrl(ringUrl)),
  addLikedRingState: (ring: IRing) => dispatch(addLikedRing(ring)),
  setLikedRingsState: (rings: IRing[]) => dispatch(setLikedRings(rings)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RingSwipe);
